import { AppIcon, AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { View, TouchableOpacity, ScrollView } from "react-native";
import ToothFairyButton from "../ToothFairyComponents/ToothFairyButton";
import ToothFairyModal from "../ToothFairyComponents/ToothFairyModal";

const VersionDiffView = ({
  isVisible,
  onClose,
  changes,
  snapshotVersion,
  currentVersion = "current",
}) => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const [expandedSections, setExpandedSections] = useState(
    Object.keys(changes?.groupedChanges || {}).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {}),
  );
  const [showUnchanged, setShowUnchanged] = useState(false);

  const toggleSection = (category) => {
    setExpandedSections((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const expandAll = () => {
    setExpandedSections(
      Object.keys(changes?.groupedChanges || {}).reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {}),
    );
  };

  const collapseAll = () => {
    setExpandedSections(
      Object.keys(changes?.groupedChanges || {}).reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {}),
    );
  };

  const categoryKeyMap = {
    "Model Settings": "versionDiff.categories.modelSettings",
    "Tools & Capabilities": "versionDiff.categories.toolsCapabilities",
    Knowledge: "versionDiff.categories.knowledge",
    Behavior: "versionDiff.categories.behavior",
    "Memory & History": "versionDiff.categories.memoryHistory",
    Moderation: "versionDiff.categories.moderation",
    "Voice & Language": "versionDiff.categories.voiceLanguage",
    Permissions: "versionDiff.categories.permissions",
    Identity: "versionDiff.categories.identity",
    "User Experience": "versionDiff.categories.userExperience",
    "Code Execution": "versionDiff.categories.codeExecution",
    Desktop: "versionDiff.categories.desktop",
    Summarisation: "versionDiff.categories.summarisation",
  };

  const categorizedChanges = useMemo(() => {
    if (!changes?.groupedChanges) return [];

    return Object.entries(changes.groupedChanges)
      .filter(
        ([category, items]) =>
          showUnchanged || items.some((c) => c.changeType !== "unchanged"),
      )
      .sort(([a], [b]) => {
        const priorityOrder = [
          "Model Settings",
          "Tools & Capabilities",
          "Knowledge",
          "Behavior",
          "Memory & History",
          "Moderation",
          "Voice & Language",
          "Permissions",
          "Identity",
          "User Experience",
          "Code Execution",
          "Desktop",
          "Summarisation",
        ];
        const aIndex = priorityOrder.indexOf(a);
        const bIndex = priorityOrder.indexOf(b);

        if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;
        return a.localeCompare(b);
      });
  }, [changes, showUnchanged]);

  const diffColors = {
    addition: currentTheme?.theme?.successColor || "#28a745",
    deletion: currentTheme?.theme?.dangerColor || "#dc3545",
    modification: currentTheme?.theme?.warningColor || "#ffc107",
    unchanged: currentTheme?.theme?.grey || "#6c757d",
  };

  const renderDiffLine = (prefix, content, color, indent = 0) => {
    return (
      <View
        style={{
          flexDirection: "row",
          fontFamily: "monospace",
          fontSize: 13,
          backgroundColor:
            color === diffColors.deletion
              ? `${diffColors.deletion}15`
              : color === diffColors.addition
              ? `${diffColors.addition}15`
              : "transparent",
          paddingVertical: 3,
          paddingHorizontal: 8,
          marginLeft: indent,
        }}
      >
        <AppText
          preventTranslation
          style={{
            color,
            fontFamily: "monospace",
            fontWeight: "bold",
            width: 20,
          }}
        >
          {prefix}
        </AppText>
        <View style={{ flex: 1, flexWrap: "wrap" }}>
          <AppText
            preventTranslation
            style={{
              color: currentTheme?.theme?.fontColor,
              fontFamily: "monospace",
              flexWrap: "wrap",
              fontSize: currentTheme?.theme?.extra_small_font_size,
            }}
          >
            {content}
          </AppText>
        </View>
      </View>
    );
  };

  const renderStringWithHighlights = (diffSegments, highlightType) => {
    return (
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={true}
        style={{ maxHeight: 300 }}
        contentContainerStyle={{ paddingRight: 10 }}
      >
        <View style={{ flexDirection: "row", flexWrap: "wrap", flex: 1 }}>
          {diffSegments.map((segment, index) => {
            const shouldHighlight =
              segment.type === highlightType || segment.type === "unchanged";
            const isHighlighted = segment.type === highlightType;

            if (!shouldHighlight && segment.type !== "unchanged") {
              return null;
            }

            const bgColor = isHighlighted
              ? highlightType === "removed"
                ? `${diffColors.deletion}25`
                : `${diffColors.addition}25`
              : "transparent";
            const textColor = isHighlighted
              ? highlightType === "removed"
                ? diffColors.deletion
                : diffColors.addition
              : currentTheme?.theme?.fontColor;

            return (
              <AppText
                key={index}
                preventTranslation
                style={{
                  backgroundColor: bgColor,
                  color: textColor,
                  fontFamily: "monospace",
                  fontSize: currentTheme?.theme?.extra_small_font_size,
                  textDecorationLine:
                    highlightType === "removed" && isHighlighted
                      ? "line-through"
                      : "none",
                }}
              >
                {segment.value}
              </AppText>
            );
          })}
        </View>
      </ScrollView>
    );
  };

  const renderFieldDiff = (change) => {
    if (change.changeType === "added") {
      return (
        <View key={change.field}>
          {renderDiffLine("+", `${change.label}:`, diffColors.addition)}
          <View style={{ marginLeft: 20 }}>
            {renderDiffLine("", change.newDisplay, diffColors.addition, 20)}
          </View>
        </View>
      );
    } else if (change.changeType === "removed") {
      return (
        <View key={change.field}>
          {renderDiffLine("-", `${change.label}:`, diffColors.deletion)}
          <View style={{ marginLeft: 20 }}>
            {renderDiffLine("", change.oldDisplay, diffColors.deletion, 20)}
          </View>
        </View>
      );
    } else if (change.changeType === "modified") {
      if (change.stringDiff) {
        return (
          <View key={change.field}>
            {renderDiffLine("~", `${change.label}:`, diffColors.modification)}
            <View style={{ marginLeft: 20, marginTop: 6, marginBottom: 6 }}>
              <View style={{ marginBottom: 4 }}>
                {renderStringWithHighlights(change.stringDiff, "removed")}
              </View>
              <View style={{ marginTop: 4 }}>
                {renderStringWithHighlights(change.stringDiff, "added")}
              </View>
            </View>
          </View>
        );
      }

      return (
        <View key={change.field}>
          {renderDiffLine("~", `${change.label}:`, diffColors.modification)}
          <View style={{ marginLeft: 20 }}>
            {renderDiffLine("-", change.oldDisplay, diffColors.deletion, 20)}
            {renderDiffLine("+", change.newDisplay, diffColors.addition, 20)}
          </View>
        </View>
      );
    } else if (change.changeType === "unchanged" && showUnchanged) {
      return (
        <View key={change.field}>
          {renderDiffLine(
            " ",
            `${change.label}: ${change.oldDisplay}`,
            diffColors.unchanged,
          )}
        </View>
      );
    }
    return null;
  };

  const renderCategorySection = ([category, items]) => {
    const isExpanded = expandedSections[category];
    const relevantItems = items.filter(
      (c) => showUnchanged || c.changeType !== "unchanged",
    );

    if (relevantItems.length === 0) return null;

    return (
      <View key={category} style={{ marginBottom: 16 }}>
        <TouchableOpacity
          onPress={() => toggleSection(category)}
          style={{
            flexDirection: "row",
            alignItems: "center",
            backgroundColor:
              currentTheme?.theme?.softBackgroundColor || "#f5f5f5",
            paddingVertical: 10,
            paddingHorizontal: 12,
            borderRadius: 6,
            marginBottom: 8,
          }}
        >
          <AppIcon
            icon={
              isExpanded
                ? AppAssets.icons.arrow_down
                : AppAssets.icons.arrow_right
            }
            size={16}
            color={currentTheme?.theme?.primary}
          />
          <AppText
            style={{
              fontSize: 14,
              fontWeight: "bold",
              color: currentTheme?.theme?.fontColor,
              marginLeft: 8,
            }}
          >
            {t(categoryKeyMap[category] || category)}
          </AppText>
        </TouchableOpacity>

        {isExpanded && (
          <View
            style={{
              // backgroundColor:
              //   currentTheme?.theme?.inputBackgroundColor || "#fafafa",
              borderRadius: 6,
              padding: 8,
              borderLeftWidth: 3,
              borderLeftColor: currentTheme?.theme?.primary,
            }}
          >
            {relevantItems.map((change) => renderFieldDiff(change))}
          </View>
        )}
      </View>
    );
  };

  return (
    <ToothFairyModal
      isModalOpen={isVisible}
      setIsModalOpen={onClose}
      modalStyle={{
        maxWidth: 700,
        width: "95%",
        height: "85%",
      }}
      modalTitle={t("versionDiff.diffViewTitle", { version: snapshotVersion })}
      closeModal={onClose}
    >
      <View style={{ flex: 1, padding: 20 }}>
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 16,
            paddingVertical: 10,
            paddingHorizontal: 12,
            backgroundColor:
              currentTheme?.theme?.softBackgroundColor || "#f5f5f5",
            borderRadius: 6,
          }}
        >
          <View style={{ flexDirection: "row", alignItems: "center", gap: 16 }}>
            <View
              style={{ flexDirection: "row", alignItems: "center", gap: 6 }}
            >
              <View
                style={{
                  width: 12,
                  height: 12,
                  backgroundColor: diffColors.addition,
                  borderRadius: 2,
                }}
              />
              <AppText
                style={{ fontSize: 12, color: currentTheme?.theme?.green }}
              >
                {t("versionDiff.added")}
              </AppText>
            </View>
            <View
              style={{ flexDirection: "row", alignItems: "center", gap: 6 }}
            >
              <View
                style={{
                  width: 12,
                  height: 12,
                  backgroundColor: diffColors.deletion,
                  borderRadius: 2,
                }}
              />
              <AppText
                style={{ fontSize: 12, color: currentTheme?.theme?.red }}
              >
                {t("versionDiff.removed")}
              </AppText>
            </View>
            <View
              style={{ flexDirection: "row", alignItems: "center", gap: 6 }}
            >
              <View
                style={{
                  width: 12,
                  height: 12,
                  backgroundColor: diffColors.modification,
                  borderRadius: 2,
                }}
              />
              <AppText
                style={{ fontSize: 12, color: currentTheme?.theme?.orange }}
              >
                {t("versionDiff.modified")}
              </AppText>
            </View>
          </View>
          <View style={{ flexDirection: "row", gap: 8 }}>
            <TouchableOpacity
              onPress={expandAll}
              style={{
                paddingHorizontal: 10,
                paddingVertical: 4,
                borderRadius: 4,
                backgroundColor: currentTheme?.theme?.primary,
              }}
            >
              <AppText
                style={{
                  fontSize: 12,
                  color: currentTheme?.theme?.white || "white",
                }}
              >
                {t("versionDiff.expandAll")}
              </AppText>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={collapseAll}
              style={{
                paddingHorizontal: 10,
                paddingVertical: 4,
                borderRadius: 4,
                backgroundColor: currentTheme?.theme?.mediumGrey,
              }}
            >
              <AppText
                style={{
                  fontSize: 12,
                  color: currentTheme?.theme?.white || "white",
                }}
              >
                {t("versionDiff.collapseAll")}
              </AppText>
            </TouchableOpacity>
          </View>
        </View>

        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{ flexGrow: 1 }}
          showsVerticalScrollIndicator={false}
        >
          {categorizedChanges.map((entry) => renderCategorySection(entry))}
        </ScrollView>

        <View
          style={{
            flexDirection: "row",
            justifyContent: "flex-end",
            gap: 10,
            marginTop: 20,
            paddingTop: 20,
            borderTopWidth: 1,
            borderTopColor: currentTheme?.theme?.border_color || "#e0e0e0",
          }}
        >
          <ToothFairyButton
            title={t("versionDiff.close")}
            onPress={onClose}
            wrapperStyle={{ minWidth: 100 }}
          />
        </View>
      </View>
    </ToothFairyModal>
  );
};

export default VersionDiffView;
